import ConditionalWrapper from '@/components/ui/custom/ConditionalWrapper';
import IconButton from '@/components/ui/custom/buttons/IconButton';
import { Tooltip } from '@/components/ui/custom/dataDisplay/Tooltip';
import SecondaryInput from '@/components/ui/custom/secondary/SecondaryInput';
import { DragIcon } from '@/components/icons';
import { useAutoFocus } from '@/data/hook/useAutoFocus';
import { StudioPermissions } from '@/enums/VersioningStatus';
import { TempLesson } from '@/models/Lesson';
import { MatrixBlockType } from '@/models/_deprecated/LessonBlock';
import { secondaryInput } from '@/styles/styles';
import alert from '@/utils/shared/alert';
import { Draggable, Droppable } from '@hello-pangea/dnd';
import { TrashIcon } from '@heroicons/react/24/outline';
import { useImperativeHandle } from 'react';
import { zeroPad } from 'react-countdown';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { twJoin, twMerge } from 'tailwind-merge';
import { CourseStudioFormFields } from '../CourseStudio.hooks';
import { CourseStudioProps } from '../info/CourseStudioInfo.hooks';
import { LESSON_BLOCKS_LIMIT } from '@/constants';

type LessonsOnlyMatrixProps = CourseStudioProps & {
  studioPermissions: StudioPermissions;
  onAddLesson: React.RefObject<() => void>;
};

export function DirectLessonMatrix(props: LessonsOnlyMatrixProps) {
  const { studioPermissions, onAddLesson } = props;

  const { control, watch } = useFormContext<CourseStudioFormFields>();

  const { t } = useTranslation('translation', {
    keyPrefix: 'courseStudio',
  });

  const courseBaseLessonsQuantity = watch('coursePath.lessonsQuantity');

  const {
    fields: lessons,
    append,
    remove,
  } = useFieldArray({
    control,
    name: `directLessons`,
    keyName: 'tempId',
    rules: {
      maxLength: courseBaseLessonsQuantity ?? LESSON_BLOCKS_LIMIT,
    },
  });

  const { setElementRef: setLessonElementRef } = useAutoFocus(lessons.length);

  useImperativeHandle(onAddLesson, () => handleAddLesson);

  if (lessons.length === 0) return null;

  const hasMaxLessons = () => {
    const lessonsCount = lessons.length;

    if (!courseBaseLessonsQuantity) return false;

    return lessonsCount >= courseBaseLessonsQuantity;
  };

  const handleAddLesson = () => {
    if (hasMaxLessons()) {
      return alert.error(t('maxLessonsReached'));
    }
    const newLesson = new TempLesson({ order: lessons.length + 1 });
    append(newLesson);
  };

  const handleRemoveLesson = (index: number) => {
    remove(index);
  };

  const { allowOtherSteps } = studioPermissions;

  const allowAdvancedEditing =
    allowOtherSteps.advancedEditing || allowOtherSteps.createEditing;

  return (
    <li className="flex flex-col grow gap-2.5">
      <Droppable
        droppableId="lessonsOnly"
        type={MatrixBlockType.LESSON}
        isDropDisabled={!allowAdvancedEditing}
      >
        {provided => (
          <ul
            ref={provided.innerRef}
            {...provided.droppableProps}
            className={twJoin(
              'flex flex-col gap-2.5 min-h-[2rem]',
              'transition-all duration-200 ease-in-out',
            )}
          >
            {lessons.map((lesson, lessonIndex) => {
              return (
                <DraggableItem
                  key={String(lesson.tempId || lesson.id)}
                  lesson={lesson}
                  index={lessonIndex}
                  removeLesson={() => handleRemoveLesson(lessonIndex)}
                  studioPermissions={studioPermissions}
                  globalLessonIndex={lesson.order - 1}
                  setLessonElementRef={setLessonElementRef}
                />
              );
            })}
            {provided.placeholder}
          </ul>
        )}
      </Droppable>
    </li>
  );
}

type DraggableItemProps = {
  lesson: TempLesson;
  index: number;
  removeLesson: () => void;
  studioPermissions: StudioPermissions;
  globalLessonIndex: number;
  setLessonElementRef: (index: number) => (el: HTMLInputElement | null) => void;
};

export function DraggableItem(props: DraggableItemProps) {
  const {
    lesson,
    index,
    removeLesson,
    studioPermissions,
    globalLessonIndex,
    setLessonElementRef,
  } = props;

  const { control } = useFormContext<CourseStudioFormFields>();

  const { t } = useTranslation('translation', {
    keyPrefix: 'courseStudio',
  });

  const { t: tCommon } = useTranslation('translation', {
    keyPrefix: 'common',
  });

  const { allowOtherSteps } = studioPermissions;

  const allowSimpleEditing =
    allowOtherSteps.simpleEditing ||
    allowOtherSteps.createEditing ||
    allowOtherSteps.advancedEditing;

  const allowAdvancedEditing =
    allowOtherSteps.advancedEditing || allowOtherSteps.createEditing;

  const disabled = !allowSimpleEditing && !allowAdvancedEditing;

  return (
    <Draggable
      draggableId={String(lesson.tempId || lesson.id)}
      index={index}
      isDragDisabled={!allowAdvancedEditing}
    >
      {provided => (
        <li ref={provided.innerRef} {...provided.draggableProps}>
          <div className="flex items-center gap-2 justify-between w-full">
            <div {...provided.dragHandleProps}>
              <ConditionalWrapper
                condition={!allowAdvancedEditing}
                wrapper={
                  <Tooltip
                    text={t('notAllowedLimitedEditing')}
                    classNameContainer="flex w-full"
                  />
                }
              >
                <DragIcon className="w-4 h-4" />
              </ConditionalWrapper>
            </div>
            <fieldset
              className={twMerge(
                secondaryInput,
                'flex p-0 w-full items-center px-2.5',
                'transition-all duration-200 ease-in-out',
              )}
            >
              <label className="text-sm text-nowrap">
                {tCommon('lesson', {
                  order: zeroPad(globalLessonIndex + 1, 2),
                })}{' '}
                -
              </label>
              <Controller
                control={control}
                name={`directLessons.${index}.name`}
                render={({ field: { onChange, value = '' } }) => (
                  <ConditionalWrapper
                    condition={disabled}
                    wrapper={
                      <Tooltip
                        text={t('notAllowedIfNotAtLeastInLimitedEdition')}
                        classNameContainer="flex w-full"
                      />
                    }
                  >
                    <SecondaryInput
                      ref={setLessonElementRef(index)}
                      value={value}
                      onChange={onChange}
                      disabled={disabled}
                      className={{
                        base: 'border-none p-0 items-center',
                        button: disabled ? 'hidden' : '',
                      }}
                      required
                      error={!value}
                      clearWrapper={
                        <ConditionalWrapper
                          condition={
                            !studioPermissions.allowOtherSteps.advancedEditing
                          }
                          wrapper={
                            <Tooltip
                              text={t('notAllowedLimitedEditing')}
                              classNameContainer="flex"
                            />
                          }
                        >
                          <IconButton
                            disabled={
                              !studioPermissions.allowOtherSteps.advancedEditing
                            }
                            type="button"
                            testId="removeLessonButton"
                            icon={
                              <TrashIcon className="text-error w-5 h-5 stroke-[1.5] hover:text-error/50 transition-all duration-200 ease-in-out" />
                            }
                            onClick={removeLesson}
                          />
                        </ConditionalWrapper>
                      }
                    />
                  </ConditionalWrapper>
                )}
              />
            </fieldset>
          </div>
        </li>
      )}
    </Draggable>
  );
}
